import { ReactNode } from "react";
import { Avatar, Icon, Typography } from "@/components/atoms";
import { useGuestBooking } from "@/components/pages/Guest/GuestBookingContext";
import { useAppSelector } from "@/redux/hooks";
import { selectBookingPassenger, selectGuestContact } from "@/redux/slices/clientBooking/selectors";
import { useAccountHolder } from "../useAccountHolder";

/** "First Last", trimmed; empty string when neither part is set (never "undefined undefined"). */
const fullName = (first?: string, last?: string) => `${first ?? ""} ${last ?? ""}`.trim();

/** Avatar/icon + name/email/phone. email + phone render only when present — no empty/undefined lines. */
const Body = ({ leading, name, email, phone }: { leading: ReactNode; name: string; email?: string; phone?: string }) => (
  <div className="flex items-start gap-2.5">
    {leading}
    <div className="flex min-w-0 flex-col gap-0.5">
      <Typography variant="small" className="block truncate">
        {name}
      </Typography>
      {email && (
        <Typography variant="small-regular" className="block truncate text-neutral-dark-gray">
          {email}
        </Typography>
      )}
      {phone && (
        <Typography variant="small-regular" className="block text-neutral-dark-gray">
          {phone}
        </Typography>
      )}
    </div>
  </div>
);

/** Fallback avatar when there's no account holder photo (guest flow, or an added guest passenger). */
const ProfileIcon = () => (
  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-neutral-mid-gray">
    <Icon name="Profile" size="sm" variant="Bold" className="text-neutral-silver-gray" />
  </span>
);

/**
 * Who the booking is for — avatar + name/email/phone. Shared by both Booking Summary variants so the
 * row reads identically on the draft steps and the priced Payment step.
 *
 * Guest flow (`useGuestBooking().isGuest`): the passenger is the guest themselves, named from the
 * contact details they entered (`guestContact`) — no profile, no "(You)" suffix. Authed flow
 * (unchanged): `null`/self ⇒ the account holder ("You"), otherwise the chosen guest passenger.
 * When there's no name yet the row renders nothing, so the Trip Details card can show its empty state.
 */
export const PassengerRow = () => {
  const { isGuest } = useGuestBooking();
  const passenger = useAppSelector(selectBookingPassenger);
  const accountHolder = useAccountHolder();
  const guestContact = useAppSelector(selectGuestContact);

  if (isGuest) {
    const name = fullName(guestContact?.firstName, guestContact?.lastName);
    if (!name) return null;
    return <Body leading={<ProfileIcon />} name={name} email={guestContact?.email} phone={guestContact?.phone} />;
  }

  const isSelf = !passenger || passenger.kind === "self";
  const baseName = isSelf ? fullName(accountHolder.firstName, accountHolder.lastName) : fullName(passenger.firstName, passenger.lastName);
  if (!baseName) return null;

  const name = isSelf ? `${baseName}${accountHolder.selfSuffix}` : `${baseName} (Guest)`;
  const email = isSelf ? accountHolder.email : passenger.email;
  const phone = isSelf ? accountHolder.mobile : passenger.phone;
  const leading = isSelf ? <Avatar size="xs" src={accountHolder.avatar} alt={name} /> : <ProfileIcon />;

  return <Body leading={leading} name={name} email={email} phone={phone} />;
};
